import { useCallback, useEffect, useRef, useState } from 'react';

import clamp from 'lodash-es/clamp';

import useDebouncedCallback from '@/hooks/useDebouncedCallback';

export type CanvasTransform = { x: number; y: number; scale: number };

type Options = { minScale?: number; maxScale?: number };

// A pan never starts on top of an interactive element: tiles (so react-dnd keeps full control
// of dragging), toolbar buttons, or form controls. Everything else is pannable canvas.
const NO_PAN_SELECTOR = '.dialog-node-tile, button, a, input, textarea, select, [role="button"]';
// Multiplicative zoom per unit of wheel delta. Because zoom is proportional to scroll
// magnitude (not a fixed step), it feels right on both trackpad and mouse. Tune for feel.
const WHEEL_ZOOM_SENSITIVITY = 0.0015;
// A single wheel event can't move the zoom more than this, so a hard flick won't lurch.
const MAX_WHEEL_DELTA = 50;
// Wheel/two-finger pan speed relative to the raw scroll delta (<1 damps the trackpad). Tune for feel.
const WHEEL_PAN_SPEED = 0.5;
// Per-click zoom factor for the toolbar buttons (20% in / out).
const BUTTON_ZOOM_FACTOR = 1.2;
// Leave a margin around the graph when fitting it to the viewport.
const FIT_PADDING = 0.9;
// Pointer must move at least this many pixels before a press becomes a pan (so clicks select).
const PAN_THRESHOLD_PX = 3;
// How long to keep the canvas GPU-promoted after the last transform mutation before demoting it.
const INTERACTION_SETTLE_MS = 150;
// Top gap when a dialog opens top-aligned instead of centred.
const INITIAL_TOP_PADDING = 32;

/**
 * Pure core of the zoom: returns the transform that applies `nextScaleRaw` (clamped) while
 * keeping the content point currently under (anchorX, anchorY) fixed on screen.
 */
export const getAnchoredTransform = (
  current: CanvasTransform,
  nextScaleRaw: number,
  anchorX: number,
  anchorY: number,
  minScale: number,
  maxScale: number
): CanvasTransform => {
  const scale = clamp(nextScaleRaw, minScale, maxScale);
  if (scale === current.scale) {
    return current;
  }
  const contentX = (anchorX - current.x) / current.scale;
  const contentY = (anchorY - current.y) / current.scale;
  return { scale, x: anchorX - contentX * scale, y: anchorY - contentY * scale };
};

/**
 * Infinite pan/zoom canvas. Owns the transform in a ref and applies it imperatively to the
 * content element so panning/zooming never re-renders the node tree; only the zoom-percentage
 * readout is React state.
 */
export const useInfiniteCanvas = ({ minScale = 0.25, maxScale = 2 }: Options = {}) => {
  const viewportRef = useRef<HTMLDivElement>(null);
  const contentRef = useRef<HTMLDivElement>(null);
  const transformRef = useRef<CanvasTransform>({ x: 0, y: 0, scale: 1 });
  // Set true once a pointer drag actually moves, so the trailing click does not clear selection.
  const didPanRef = useRef(false);
  const [zoomPercent, setZoomPercent] = useState(100);

  // Demote the canvas off its GPU layer once interaction settles, so the resting DOM repaints crisp.
  const demoteCanvas = useDebouncedCallback(
    () => viewportRef.current?.classList.remove('is-interacting'),
    INTERACTION_SETTLE_MS,
    []
  );

  // Promote the canvas to a GPU layer during a gesture; called from setTransform on every mutation.
  const flagInteracting = useCallback(() => {
    viewportRef.current?.classList.add('is-interacting');
    demoteCanvas();
  }, [demoteCanvas]);

  const applyTransform = useCallback(() => {
    const el = contentRef.current;
    if (!el) {
      return;
    }
    const { x, y, scale } = transformRef.current;
    // Snap the translate to whole device pixels; fractional offsets resample blurry on 1x/low DPR.
    const dpr = window.devicePixelRatio || 1;
    const snap = (v: number) => Math.round(v * dpr) / dpr;
    el.style.transform = `translate(${snap(x)}px, ${snap(y)}px) scale(${scale})`;
  }, []);

  const setTransform = useCallback(
    (next: CanvasTransform) => {
      transformRef.current = next;
      flagInteracting();
      applyTransform();
      const percent = Math.round(next.scale * 100);
      setZoomPercent((prev) => (prev === percent ? prev : percent));
    },
    [applyTransform, flagInteracting]
  );

  const zoomToAnchor = useCallback(
    (nextScaleRaw: number, anchorX: number, anchorY: number) => {
      setTransform(
        getAnchoredTransform(
          transformRef.current,
          nextScaleRaw,
          anchorX,
          anchorY,
          minScale,
          maxScale
        )
      );
    },
    [maxScale, minScale, setTransform]
  );

  // Toolbar zoom anchors on the viewport centre.
  const zoomByFactor = useCallback(
    (factor: number) => {
      const viewport = viewportRef.current;
      if (!viewport) {
        return;
      }
      const rect = viewport.getBoundingClientRect();
      zoomToAnchor(transformRef.current.scale * factor, rect.width / 2, rect.height / 2);
    },
    [zoomToAnchor]
  );

  const onZoomInClick = useCallback(() => zoomByFactor(BUTTON_ZOOM_FACTOR), [zoomByFactor]);
  const onZoomOutClick = useCallback(() => zoomByFactor(1 / BUTTON_ZOOM_FACTOR), [zoomByFactor]);

  // Centre the graph in the viewport. `fit` scales it to fully fit; `topAlign` pins it near the top.
  const frame = useCallback(
    (fit: boolean, topAlign = false) => {
      const viewport = viewportRef.current;
      const child = contentRef.current?.firstElementChild as HTMLElement | null;
      if (!viewport || !child) {
        return;
      }
      const rect = viewport.getBoundingClientRect();
      // offsetWidth/Height are layout sizes, unaffected by the CSS transform.
      const { offsetWidth: width, offsetHeight: height } = child;
      const scale = fit
        ? clamp(Math.min(rect.width / width, rect.height / height) * FIT_PADDING, minScale, 1)
        : 1;
      const centeredY = Math.max(0, (rect.height - height * scale) / 2);
      setTransform({
        scale,
        x: (rect.width - width * scale) / 2,
        y: topAlign ? Math.min(INITIAL_TOP_PADDING, centeredY) : centeredY,
      });
    },
    [minScale, setTransform]
  );

  const onFitClick = useCallback(() => frame(true, true), [frame]);
  const frameInitial = useCallback((topAlign = false) => frame(false, topAlign), [frame]);

  // Returns whether the last gesture was a pan (and resets the flag), so the caller can skip
  // the click handler that would otherwise fire after a drag.
  const consumePan = useCallback(() => {
    const panned = didPanRef.current;
    didPanRef.current = false;
    return panned;
  }, []);

  // Wheel: ctrl/meta (and trackpad pinch, which sets ctrlKey) zoom; everything else pans.
  useEffect(() => {
    const viewport = viewportRef.current;
    if (!viewport) {
      return;
    }
    const onWheel = (e: WheelEvent) => {
      e.preventDefault();
      if (e.ctrlKey || e.metaKey) {
        const rect = viewport.getBoundingClientRect();
        const delta = clamp(e.deltaY, -MAX_WHEEL_DELTA, MAX_WHEEL_DELTA);
        const factor = Math.exp(-delta * WHEEL_ZOOM_SENSITIVITY);
        zoomToAnchor(
          transformRef.current.scale * factor,
          e.clientX - rect.left,
          e.clientY - rect.top
        );
      } else {
        setTransform({
          ...transformRef.current,
          x: transformRef.current.x - e.deltaX * WHEEL_PAN_SPEED,
          y: transformRef.current.y - e.deltaY * WHEEL_PAN_SPEED,
        });
      }
    };
    viewport.addEventListener('wheel', onWheel, { passive: false });
    return () => viewport.removeEventListener('wheel', onWheel);
  }, [setTransform, zoomToAnchor]);

  // Pointer drag-to-pan, starting only on empty canvas (never on an interactive element).
  useEffect(() => {
    const viewport = viewportRef.current;
    if (!viewport) {
      return;
    }
    // `candidate`: pointer is down on pannable canvas. `panning`: it has moved enough to pan.
    let candidate = false;
    let panning = false;
    let startX = 0;
    let startY = 0;
    let originX = 0;
    let originY = 0;

    const onPointerDown = (e: PointerEvent) => {
      if (e.button !== 0 || (e.target as HTMLElement).closest(NO_PAN_SELECTOR)) {
        return;
      }
      candidate = true;
      panning = false;
      didPanRef.current = false;
      startX = e.clientX;
      startY = e.clientY;
      originX = transformRef.current.x;
      originY = transformRef.current.y;
      // Capture immediately so Windows doesn't fire pointercancel mid-drag.
      viewport.setPointerCapture(e.pointerId);
    };

    const onPointerMove = (e: PointerEvent) => {
      if (!candidate) {
        return;
      }
      const dx = e.clientX - startX;
      const dy = e.clientY - startY;
      // Only commit to a pan once past the threshold.
      if (!panning) {
        if (Math.hypot(dx, dy) < PAN_THRESHOLD_PX) {
          return;
        }
        panning = true;
        didPanRef.current = true;
        viewport.classList.add('is-panning');
      }
      setTransform({ ...transformRef.current, x: originX + dx, y: originY + dy });
    };

    const endPan = (e: PointerEvent) => {
      if (!candidate) {
        return;
      }
      candidate = false;
      if (viewport.hasPointerCapture(e.pointerId)) {
        viewport.releasePointerCapture(e.pointerId);
      }
      if (panning) {
        panning = false;
        viewport.classList.remove('is-panning');
      }
    };

    // Block the browser's native drag so Windows doesn't ghost-drag canvas content
    // and fire pointercancel mid-pan. Allow drags that start on interactive tiles
    // so react-dnd keeps working.
    const onDragStart = (e: Event) => {
      if (!(e.target as HTMLElement).closest(NO_PAN_SELECTOR)) {
        e.preventDefault();
      }
    };

    viewport.addEventListener('pointerdown', onPointerDown);
    viewport.addEventListener('pointermove', onPointerMove);
    viewport.addEventListener('pointerup', endPan);
    viewport.addEventListener('pointercancel', endPan);
    viewport.addEventListener('dragstart', onDragStart);
    return () => {
      viewport.removeEventListener('pointerdown', onPointerDown);
      viewport.removeEventListener('pointermove', onPointerMove);
      viewport.removeEventListener('pointerup', endPan);
      viewport.removeEventListener('pointercancel', endPan);
      viewport.removeEventListener('dragstart', onDragStart);
    };
  }, [setTransform]);

  return {
    viewportRef,
    contentRef,
    zoomPercentage: `${zoomPercent}%`,
    onZoomInClick,
    onZoomOutClick,
    onFitClick,
    frameInitial,
    consumePan,
  };
};
